/**
 * Chat Configuration for Development and Production
 * Handles API configuration based on environment
 */

import loadStore from "@/utils/generateStore";

export interface ChatConfig {
  baseUrl: string;
  token: string;
  businessId: string;
}

/**
 * Base URL de trd-agent DETRÁS del PEP del BFF (trd-bff#125): el front habla SÓLO
 * al BFF (`{VITE_BFF_URL}/api/v1/agent/*`), que valida el token de identity y
 * forwardea el contexto firmado. Ya NO hay llamada directa al agent (ni su env var).
 * `VITE_BFF_URL` es el root (sin `/api/v1`), igual que `BFF_API_BASE`.
 */
const getBaseUrl = (): string => {
  return `${import.meta.env.VITE_BFF_URL}/api/v1/agent`;
};

/**
 * Get JWT token from Tauri store or environment
 * Priority: Tauri store > env variable
 */
const getToken = async (): Promise<string> => {
  // First, try to get token from Tauri store (real user token)
  try {
    const store = await loadStore();
    const token = await store.get("token");
    if (token) {
      return token;
    }
  } catch (error) {
    console.error("Error getting token from store:", error);
  }

  // Fallback: In development, use test token from env if available
  if (import.meta.env.DEV && import.meta.env.VITE_TEST_JWT_TOKEN) {
    return import.meta.env.VITE_TEST_JWT_TOKEN;
  }

  return "";
};

/**
 * Get business ID from user context
 * Priority: user.businessId > env variable
 * Must be called with user object from useAuthUser()
 */
const getBusinessIdFromUser = (user: any): string => {
  // First, try to get businessId from authenticated user
  if (user?.businessId) {
    return user.businessId;
  }

  // Fallback: In development, use test businessId from env if available
  if (import.meta.env.DEV && import.meta.env.VITE_TEST_BUSINESS_ID) {
    return import.meta.env.VITE_TEST_BUSINESS_ID;
  }

  return "";
};

/**
 * Get chat configuration
 * Async version - use when you need to await token from store
 */
export const getChatConfigAsync = async (user: any): Promise<ChatConfig> => {
  const baseUrl = getBaseUrl();
  const token = await getToken();
  const businessId = getBusinessIdFromUser(user);

  return {
    baseUrl,
    token,
    businessId,
  };
};

/**
 * Get chat configuration (synchronous version)
 * Use this when you already have the token available
 * For use in React components with useAuthUser
 */
export const getChatConfig = (
  user: any,
  tokenFromStore?: string
): ChatConfig => {
  const baseUrl = getBaseUrl();

  // Use provided token (from Tauri store) or fallback to test token from env
  let token = tokenFromStore || "";
  if (!token && import.meta.env.DEV && import.meta.env.VITE_TEST_JWT_TOKEN) {
    token = import.meta.env.VITE_TEST_JWT_TOKEN;
  }

  const businessId = getBusinessIdFromUser(user);

  return {
    baseUrl,
    token,
    businessId,
  };
};

/**
 * Check if chat is properly configured
 */
export const isChatConfigured = (config: ChatConfig): boolean => {
  return !!(config.token && config.businessId);
};
